<!-- 
Public levy estimator page.
Displays the public levy estimator for a levy collection. The public levy estimator lets the user enter all the levy
parameters and will then calculate the levy.
It is publicly visible once it has been published. 
It will no longer be visible to the public once the levy collection is finalised.
-->
<template>
  <div>
    <v-layout v-if="isSignedIn && loaded" row justify-space-between>
      <v-flex xs3>
        <v-btn 
          :to="`/levy-collections/${levyCollection.id}/setup/levy-estimator`" 
          flat color="primary" class="ml-0"
        >
          <v-icon class="mr-2">arrow_back</v-icon>
          Go to levy collection
        </v-btn>
      </v-flex>
      <v-flex xs3 class="">
        &nbsp;
      </v-flex>
    </v-layout>
    <v-card v-if="unavailable">
      <div class="subheading text-xs-center pa-5">
        {{ unavailable }}
      </div>
    </v-card>
    <v-card v-else>
      <div v-if="loaded">
        <div class="text-xs-center mb-3 pt-4">
          <img src="@/assets/aus-govt-logo-centered.png" alt="Australian Government" height="150px">
        </div>
        <h1 class="display-2 text-xs-center">
          {{ title }}
        </h1>
      </div>
      <v-card-text>
        <our-loading-indicator v-if="loading" />
        <v-scale-transition hide-on-leave>
          <playground-parameters-panel
            v-if="!loading && !showCalculation"
            :levy-collection="levyCollection"
            :buttons-disabled="state.loading || state.processing"
            for-public-estimator
            @click:recalculate="recalculate"
          />
        </v-scale-transition>
        <v-scale-transition hide-on-leave>
          <levy-calculation-panel
            v-if="!processing && showCalculation"
            :levy-collection="levyCollection"
            :levy-collection-provider="levyCollectionProvider"
            mode="public-levy-estimator"
            @click:edit="showCalculation = false"
          />
        </v-scale-transition>
      </v-card-text>
    </v-card>
    <disclaimer-copyright-and-privacy-links />
  </div>
</template>

<script>
import lookupMixins from "@/mixins/lookup.mixins";
import PlaygroundParametersPanel from "@/components/levy-collection-providers/calculation/PlaygroundParametersPanel.vue";
import LevyCalculationPanel from "@/components/levy-collection-providers/calculation/LevyCalculationPanel.vue";
import DisclaimerCopyrightAndPrivacyLinks from "@/components/system/DisclaimerCopyrightAndPrivacyLinks.vue";
import * as action from "@/store/levy-estimator/action-types";

export default {
  name: "PublicLevyEstimator",
  components: { LevyCalculationPanel, PlaygroundParametersPanel, DisclaimerCopyrightAndPrivacyLinks },
  mixins: [lookupMixins],
  data() {
    return {
      showCalculation: false
    };
  },
  computed: {
    levyCollectionYear() {
      return parseInt(this.$route.params.year);
    },
    levyCollectionType() {
      return this.$route.params.type;
    },
    levyCollectionTypeDescription() {
      return this.lookups.levyCollectionTypeDescriptions[this.levyCollectionType];
    },
    state() {
      return this.$store.state.levyEstimator.publicEstimator;
    },
    loading() {
      return this.state.loading;
    },
    processing() {
      return this.state.processing;
    },
    unpublished() {
      return !this.state.data.published;
    },
    unavailable() {
      return this.state.unavailable;
    },
    levyCollection() {
      return this.state.loaded ? this.state.data.levyCollection : null;
    },
    levyCollectionProvider() {
      return this.state.loaded ? this.state.data.levyCollectionProvider : null;
    },
    loaded() {
      return this.state.loaded;
    },
    isSignedIn() {
      return this.$store.getters.isSignedIn;
    },
    title() {
      return this.loaded
        ? `${this.levyCollection.year} ${
            this.lookups.levyCollectionTypeDescriptions[this.levyCollectionType]
          } tuition protection levy estimator`
        : "";
    }
  },
  watch: {
    levyCollectionYear() {
      this.load();
    },
    levyCollectionType() {
      this.load();
    }
  },
  mounted() {
    this.load();
  },
  methods: {
    load() {
      this.$store.dispatch(action.PUBLIC_ESTIMATOR_GET, {
        year: this.levyCollectionYear,
        type: this.levyCollectionType
      });
    },
    async recalculate() {
      if (await this.$store.dispatch(action.PUBLIC_ESTIMATOR_CALCULATE)) {
        this.showCalculation = true;
      }
    }
  }
};
</script>
